/**
 * 个人设置页面
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/authApi';
import PageHeader from '../components/layout/PageHeader';


const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMessage, setPwdMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await authApi.updateProfile(formData);
      updateUser(response.data);
      setMessage({ type: 'success', text: '个人信息更新成功' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || '更新失败，请重试',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setPwdMessage({ type: 'error', text: '两次输入的新密码不一致' });
      return;
    }
    if (!pwdForm.current_password || !pwdForm.new_password) {
      setPwdMessage({ type: 'error', text: '请填写完整' });
      return;
    }
    setPwdLoading(true);
    setPwdMessage(null);
    try {
      await authApi.changePassword({
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      setPwdMessage({ type: 'success', text: '密码修改成功' });
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      setPwdMessage({
        type: 'error',
        text: error.response?.data?.detail || '修改失败，请重试',
      });
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-2">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-border">
            <PageHeader title="个人设置" />
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
            {message && (
              <div
                className={`rounded-md p-4 ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}
              >
                {message.text}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-foreground">
                用户名
              </label>
              <input
                type="text"
                id="username"
                value={user?.username || ''}
                disabled
                className="mt-1 block w-full rounded-md border-border bg-background text-muted-foreground shadow-sm sm:text-sm"
              />
              <p className="mt-1 text-sm text-muted-foreground">用户名不可修改</p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                邮箱
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-foreground">
                姓名
              </label>
              <input
                type="text"
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-foreground">
                角色
              </label>
              <input
                type="text"
                id="role"
                value={user?.role === 'admin' ? '管理员' : '普通用户'}
                disabled
                className="mt-1 block w-full rounded-md border-border bg-background text-muted-foreground shadow-sm sm:text-sm"
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground bg-white hover:bg-background"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-muted disabled:cursor-not-allowed"
              >
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          </form>

          {/* 修改密码分区 */}
          <form onSubmit={handlePasswordSubmit} className="px-6 py-4 space-y-6 border-t border-border">
            <h3 className="text-base font-semibold text-foreground">修改密码</h3>
            {pwdMessage && (
              <div
                className={`rounded-md p-4 ${
                  pwdMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                {pwdMessage.text}
              </div>
            )}
            <div>
              <label htmlFor="current_password" className="block text-sm font-medium text-foreground">当前密码</label>
              <input
                type="password"
                id="current_password"
                value={pwdForm.current_password}
                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                required
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="new_password" className="block text-sm font-medium text-foreground">新密码</label>
              <input
                type="password"
                id="new_password"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                required
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-foreground">确认新密码</label>
              <input
                type="password"
                id="confirm_password"
                value={pwdForm.confirm_password}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                required
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={pwdLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:bg-muted disabled:cursor-not-allowed"
              >
                {pwdLoading ? '修改中...' : '修改密码'}
              </button>
            </div>
          </form>
        </div>
      </div>
  );
};

export default Profile;
